import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { computed, ref } from 'vue';

import type {
  MediaEntryReference,
  MediaEntryLaunch,
} from '@/app/mediaInsightsDrawer/entry/entry.types';

import { useMediaPopupStore } from '@/stores/media-popup';

/**
 * An item from the media-popup store's heterogeneous `contextList` (media, pins,
 * deliverables, etc.). Only the fields this composable reads are declared; the
 * index signature covers the dynamic `[source]` lookup for a per-platform timestamp.
 */
interface MediaContextListItem extends MediaEntryLaunch {
  id?: number | string;
  sourceId?: number | string;
  source?: string;
  mediaReference?: MediaEntryReference;
  brandId?: number;
  brandLabel?: string;
  patchJson?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface OpenMediaArgs extends MediaEntryLaunch {
  id: number | string;
  /** The page section the post was opened from, reported on `Media Pop-up Opened`. */
  section?: string;
}

/** Props passed to the media drawer for a single post. */
interface MediaDrawerProps extends MediaEntryLaunch {
  patchJson: Record<string, unknown>;
  brandLabel?: string;
  brandId?: number;
  source?: string;
}

/** Minimal shape of the media object handed to `updatePropsListIndex`. */
interface CurrentMedia {
  id?: number | string;
  postSourceId?: number | string;
}

const propsListIndex = ref<number | null>(null);
const context = ref<Partial<OpenMediaArgs>>({});

export function useMediaDrawer() {
  const mediaPopupStore = useMediaPopupStore();
  const { launchDrawer } = useDrawer();

  const propsList = computed<MediaDrawerProps[]>(() =>
    ((mediaPopupStore.contextList ?? []) as MediaContextListItem[])?.map((contextListItem) => {
      const source = contextListItem.source;
      const mediaReference =
        contextListItem.mediaReference ??
        (!context.value.source && contextListItem.id !== undefined
          ? { kind: 'library' as const, mediaId: contextListItem.id }
          : undefined);
      const resolvedSource =
        mediaReference?.kind === 'library'
          ? undefined
          : (mediaReference?.source ?? source ?? context.value.source ?? undefined);
      const id =
        mediaReference?.kind === 'library'
          ? mediaReference.mediaId
          : (mediaReference?.sourceId ?? contextListItem.sourceId ?? contextListItem.id);

      const patchJson: Record<string, unknown> = {
        ...context.value.patchJson,
        ...contextListItem.patchJson,
        media_id: id,
      };
      const sourceItem = source
        ? (contextListItem[source] as { timestamp?: unknown } | undefined)
        : undefined;
      if (sourceItem?.timestamp) {
        patchJson.timestamp = sourceItem.timestamp;
      }

      return {
        id,
        patchJson,
        sourceId: contextListItem.sourceId,
        mediaReference,
        openOrigin: contextListItem.openOrigin ?? context.value.openOrigin,
        navigationDirection:
          contextListItem.navigationDirection ?? context.value.navigationDirection,
        reportingStartDate: context.value.reportingStartDate,
        reportingEndDate: context.value.reportingEndDate,
        brandLabel: contextListItem.brandLabel ?? context.value.brandLabel,
        brandId: contextListItem.brandId ?? context.value.brandId,
        source: resolvedSource,
        platformHint: contextListItem.platformHint ?? source,
      };
    }),
  );

  /**
   * Launch the global media drawer for a post.
   *
   * Brand context (brandId / brandLabel) falls back to the active brand inside
   * MediaPopup's data fetch, so callers can omit it when targeting the current
   * brand.
   *
   * When `source` is omitted, MediaPopup routes by library id
   * (`/brands/:bid/media/:id?allow_index=true`). When `source` is provided,
   * it routes by platform id (`/media/source/:id?source_type=...`) and uses
   * `patchJson` (with `instagram_name` / `tiktok_name` + optional `timestamp`)
   * to index the post on demand.
   */
  async function launchMedia(
    {
      id,
      brandId,
      brandLabel,
      source,
      patchJson: incomingPatchJson,
      section,
      ...launchMetadata
    }: OpenMediaArgs,
    preserveContext = false,
  ) {
    const mediaReference = launchMetadata.mediaReference;
    const resolvedSource =
      mediaReference?.kind === 'library'
        ? undefined
        : (mediaReference?.source ?? source ?? undefined);
    const selectedId =
      mediaReference?.kind === 'library'
        ? mediaReference.mediaId
        : resolvedSource
          ? (mediaReference?.sourceId ?? launchMetadata.sourceId ?? id)
          : id;
    const patchJson = { ...incomingPatchJson, media_id: selectedId };

    if (!preserveContext) {
      context.value = {
        brandId,
        brandLabel,
        source: resolvedSource,
        patchJson,
        section,
        ...launchMetadata,
      };
    }

    const selected = propsList.value.find(
      (item) =>
        item.id === selectedId && item.source?.toUpperCase() === resolvedSource?.toUpperCase(),
    );

    void launchDrawer({
      name: 'media',
      props: {
        brandId,
        brandLabel,
        patchJson,
        ...launchMetadata,
        id: selectedId,
        source: resolvedSource,
        ...selected,
        platformHint: selected?.platformHint ?? launchMetadata.platformHint,
      },
      propsList: propsList.value,
    });
  }

  /** Related records get their own navigation list; Back restores the original drawer and list. */
  async function openRelatedMedia(args: OpenMediaArgs, mediaIds: readonly number[]) {
    const propsFor = (id: number | string) => ({
      ...args,
      id,
      mediaReference: { kind: 'library' as const, mediaId: id },
    });
    return launchDrawer({
      name: 'media',
      props: propsFor(args.id),
      propsList: mediaIds.map(propsFor),
      pushHistory: true,
    });
  }

  async function openMedia(args: OpenMediaArgs) {
    await launchMedia(args);
  }

  function updatePropsListIndex(media: CurrentMedia | null | undefined) {
    if (!media || !propsList.value) {
      propsListIndex.value = null;
      return;
    }

    propsListIndex.value = propsList.value.findIndex((item) => {
      if (item.id) {
        return (
          item.id === media.id ||
          item.id === media.postSourceId ||
          (media.postSourceId !== undefined && item.sourceId === media.postSourceId)
        );
      }
      return item.sourceId === media.postSourceId;
    });
  }

  const hasPreviousMedia = computed(() => {
    if (propsList.value?.length > 0) {
      return typeof propsListIndex.value === 'number' && propsListIndex.value > 0;
    }
    return false;
  });

  const hasNextMedia = computed(() => {
    if (propsList.value?.length > 0) {
      return (
        typeof propsListIndex.value === 'number' &&
        propsListIndex.value >= 0 &&
        propsListIndex.value < propsList.value.length - 1
      );
    }
    return false;
  });

  function loadPreviousMedia() {
    if (!hasPreviousMedia.value || propsListIndex.value === null) {
      return null;
    }

    propsListIndex.value -= 1;
    const previousProps = propsList.value[propsListIndex.value];

    void launchMedia({ ...previousProps, section: context.value.section } as OpenMediaArgs, true);

    return previousProps;
  }

  function loadNextMedia() {
    if (!hasNextMedia.value || propsListIndex.value === null) {
      return null;
    }

    propsListIndex.value += 1;
    const nextProps = propsList.value[propsListIndex.value];

    void launchMedia({ ...nextProps, section: context.value.section } as OpenMediaArgs, true);

    return nextProps;
  }

  const openedFromSection = computed(() => context.value.section);

  return {
    openMedia,
    openRelatedMedia,
    openedFromSection,
    hasPreviousMedia,
    hasNextMedia,
    loadPreviousMedia,
    loadNextMedia,
    updatePropsListIndex,
  };
}
